// BALTIVEX i18n — English / Latvian / German
// Single source of truth for all marketing copy used across pages.

export const LANGUAGES = [
  { code: "en", label: "EN" },
  { code: "lv", label: "LV" },
  { code: "de", label: "DE" },
];

export const translations = {
  en: {
    nav: {
      products: "Products",
      services: "Services",
      industries: "Industries",
      innovation: "Innovation",
      sustainability: "Sustainability",
      about: "About",
      contact: "Contact",
      cta: "Start a Project",
    },
    footer: {
      tagline: "Advanced Biomaterials",
      intro:
        "European advanced materials company developing biopolymers, biocomposites and engineered compounds for industrial manufacturers across the continent.",
      cta: "Start a Project",
      col_materials: "Materials",
      col_services: "Services",
      col_company: "Company",
      links_materials: [
        "PLA Compounds",
        "Biocomposites",
        "Wood-filled Polymers",
        "Technical Compounds",
        "Masterbatch Solutions",
        "3D Printing Materials",
      ],
      links_services: [
        "Custom Compounding",
        "Material Development",
        "Pilot Production",
        "Technical Consulting",
        "Industrial Scale-Up",
      ],
      links_company: [
        "About BALTIVEX",
        "Innovation",
        "Sustainability",
        "Industries",
        "Contact",
      ],
      copyright: "BALTIVEX · Ogre district, Latvia · European Union",
    },
    home: {
      hero_eyebrow: "Advanced Biomaterials · Latvia · European Union",
      hero_l1: "Engineered materials",
      hero_l2: "for European",
      hero_l3_a: "manufacturing.",
      hero_intro:
        "BALTIVEX develops and manufactures biopolymer compounds, biocomposites and engineered formulations for the most demanding industrial applications across Europe.",
      hero_cta1: "Explore Materials",
      hero_cta2: "Request a Sample",
      metrics: [
        ["120+", "Material formulations"],
        ["18", "European partners"],
        ["1.4 kt", "Annual capacity"],
        ["EU", "Manufactured · Latvia"],
      ],

      products_eyebrow: "Materials Portfolio",
      products_title: "Premium compounds, engineered for performance.",
      products_intro:
        "From PLA biopolymers to wood-filled technical compounds, our portfolio is engineered for converters and manufacturers who demand consistency at industrial scale.",
      products_all: "All materials",

      products_grid: [
        "PLA Compounds",
        "Biocomposites",
        "Wood-filled Polymers",
        "Technical Compounds",
        "Masterbatch Solutions",
        "3D Printing Materials",
      ],

      services_eyebrow: "Industrial Services",
      services_title: "From formulation to industrial scale.",
      services_intro:
        "We partner with your engineering and procurement teams across the full material lifecycle — concept, validation, pilot, and series production.",
      services_link: "View capabilities",
      services_list: [
        "Custom Compounding",
        "Material Development",
        "Pilot Production",
        "Technical Consulting",
        "R&D Partnerships",
        "Industrial Scale-Up",
      ],

      industries_eyebrow: "Industries",
      industries_title: "Trusted across sectors that demand precision.",
      industries_intro:
        "Our materials power high-volume packaging lines, automotive interior parts, consumer products and architectural elements across the European Union.",
      industries_grid: [
        "Automotive",
        "Packaging",
        "Consumer Goods",
        "Furniture & Interiors",
      ],
      industries_industry: "Industry",
      industries_explore: "Explore",

      innovation_eyebrow: "Innovation",
      innovation_title_l1: "R&D rooted in",
      innovation_title_l2: "material science.",
      innovation_intro:
        "Our laboratories combine polymer chemistry, rheology and thermal analysis with industrial-grade pilot lines — bridging the gap between scientific research and series production.",
      innovation_link: "Inside our lab",
      innovation_labs: [
        ["DSC", "Thermal Analysis"],
        ["MFI", "Melt Flow Index"],
        ["TGA", "Decomposition"],
        ["SEM", "Microscopy"],
      ],

      why_eyebrow: "Why BALTIVEX",
      why_title: "Engineering you can specify with confidence.",
      why_cards: [
        {
          t: "European Manufacturing",
          d: "Produced in Latvia under EU industrial standards with full traceability, batch documentation and CoA on every shipment.",
        },
        {
          t: "Material Engineering",
          d: "Custom formulations developed by polymer engineers — tuned to your converter, your tool and your part.",
        },
        {
          t: "Pilot to Series",
          d: "Validated pilot lines bridge laboratory development with reliable, repeatable industrial-scale production.",
        },
        {
          t: "Responsible Innovation",
          d: "Biopolymer and natural-fibre chemistries that reduce dependency on fossil feedstocks without compromising performance.",
        },
      ],

      cta_eyebrow: "Start Your Project",
      cta_title_l1: "From your specification",
      cta_title_l2: "to a validated material —",
      cta_title_l3: "together.",
      cta_button: "Request Material Brief",
    },

    products: {
      hero_eyebrow: "Materials Portfolio",
      hero_title: "Compounds engineered for industrial performance.",
      hero_intro:
        "A complete portfolio of biopolymer, biocomposite and technical compounds — developed for converters, OEMs and product developers across Europe.",
      group_label: "Group",
      bottom_eyebrow: "Datasheets · CoA · Samples",
      bottom_title_l1: "Tell us what you are making.",
      bottom_title_l2: "We will engineer the material.",
      bottom_cta: "Start a Project",
      groups: [
        {
          name: "PLA Compounds",
          tagline: "Bio-based engineering grades",
          description:
            "Polylactic acid compounds engineered for thermoforming, injection moulding and extrusion. Tuned crystallinity, impact modification and heat deflection on request.",
          specs: ["Bio-based content > 85%", "Tg 55–62 °C", "MFI 4–24 g/10 min"],
        },
        {
          name: "Biocomposites",
          tagline: "Natural fibre reinforced polymers",
          description:
            "Flax, hemp and cellulose-reinforced compounds with engineered fibre-matrix coupling. Designed for stiffness, low density and acoustic damping.",
          specs: ["Density 1.05–1.18 g/cm³", "Flex modulus up to 6.2 GPa", "Fibre 20–40 %"],
        },
        {
          name: "Wood-filled Polymers",
          tagline: "Natural surface, technical performance",
          description:
            "Wood-flour and softwood-fibre filled compounds for furniture, interior and consumer applications. Authentic touch and matte finish with industrial repeatability.",
          specs: ["Wood content 25–55 %", "PP, PE, PLA matrices", "Tooling-friendly"],
        },
        {
          name: "Technical Compounds",
          tagline: "Mineral & glass reinforced",
          description:
            "High-performance reinforced compounds for automotive, electrical and structural applications. Tight tolerance on melt flow and shrinkage.",
          specs: ["Glass / Talc / CaCO₃", "Up to 50 % reinforcement", "Heat-stabilised grades"],
        },
        {
          name: "Masterbatch Solutions",
          tagline: "Colour, additive & functional",
          description:
            "Custom colour, UV, antistatic, slip, anti-block and processing aid masterbatches developed to your carrier, your line and your part.",
          specs: ["Pantone & RAL matching", "Food-contact options", "UV / IR / NIR grades"],
        },
        {
          name: "3D Printing Materials",
          tagline: "Industrial-grade filaments & granulates",
          description:
            "FDM filaments and pellet-based granulates for industrial additive manufacturing. Bio-based, fibre-filled and engineering grades for prototyping and end-use parts.",
          specs: ["1.75 / 2.85 mm filament", "Pellet AM granulates", "Wood, flax & PLA-X"],
        },
        {
          name: "Custom Formulations",
          tagline: "Engineered to your specification",
          description:
            "Bespoke compounds developed from your performance brief — from feedstock selection and additive design to validated industrial-scale production.",
          specs: ["NDA-protected R&D", "DOE-driven development", "Full CoA & traceability"],
        },
      ],
    },

    services: {
      hero_eyebrow: "Industrial Services",
      hero_title: "Material partners for industrial-scale manufacturing.",
      hero_intro:
        "We work with your engineering, procurement and operations teams across the full lifecycle of a compound — from a performance brief to a fully validated industrial material.",
      sidebar_eyebrow: "Capabilities",
      sidebar_title_l1: "Seven capabilities,",
      sidebar_title_l2: "one engineering partner.",
      sidebar_intro:
        "Our team is composed of polymer engineers, process technologists and material scientists — supporting your project from the first kilogram to the first container.",
      sidebar_cta: "Brief our engineers",
      process_eyebrow: "Process",
      process_title: "Brief · Develop · Pilot · Validate · Produce.",
      process_intro:
        "A repeatable engineering process that compresses time-to-market and removes risk from material adoption — designed for European industrial customers with strict documentation requirements.",
      items: [
        {
          title: "Custom Compounding",
          summary:
            "Bespoke compound development on twin-screw extrusion lines tuned for your converter and end-application.",
          points: [
            "Twin-screw co-rotating extrusion, 27–70 mm",
            "Side-stuff for fibres and sensitive additives",
            "In-line gravimetric dosing & vacuum degassing",
          ],
        },
        {
          title: "Material Development",
          summary:
            "Polymer engineers translate your performance brief into a validated, scalable material — backed by full lab characterisation.",
          points: ["DOE-driven formulation", "Failure-mode and reproducibility study", "Datasheet + CoA + SDS"],
        },
        {
          title: "Pilot Production",
          summary:
            "Bridge laboratory work with industrial reality on validated pilot lines that replicate your downstream process conditions.",
          points: ["25–500 kg trial batches", "Process windows & rheology mapping", "Tooling-friendly drying & dosing"],
        },
        {
          title: "Technical Consulting",
          summary:
            "Embedded support across material selection, machine setup, and downstream conversion — from your prototype to series.",
          points: ["Material selection workshops", "Conversion troubleshooting", "Cost-engineering reviews"],
        },
        {
          title: "Research & Development",
          summary:
            "Long-term R&D partnerships in bio-based chemistries, fibre reinforcement and functional masterbatches.",
          points: ["NDA-protected collaboration", "Horizon-style joint research", "IP-clean development paths"],
        },
        {
          title: "Material Optimisation",
          summary:
            "Re-engineer existing compounds to reduce cost, improve sustainability profile, or unlock new converter windows.",
          points: ["Bio-content uplift", "Cycle-time reduction", "Lightweighting & part consolidation"],
        },
        {
          title: "Industrial Scale-Up",
          summary:
            "Move from validated pilot to repeatable series production with batch traceability, CoA, and EU-compliant documentation.",
          points: ["1.4 kt annual capacity", "Multi-shift production", "Full European logistics network"],
        },
      ],
    },

    industries: {
      hero_eyebrow: "Industries we serve",
      hero_title: "Materials for sectors that demand precision.",
      hero_intro:
        "From automotive interiors to industrial packaging and architectural surfaces — BALTIVEX materials are specified by European manufacturers who measure quality in microns and decades.",
      sector_label: "Sector",
      bottom_eyebrow: "Cross-industry expertise",
      bottom_title: "One materials platform. Many industrial outcomes.",
      items: [
        {
          name: "Automotive",
          intro:
            "Interior trims, under-the-hood components and structural parts. Bio-reinforced compounds delivering weight and CO₂ reduction without compromising performance.",
          bullets: ["IATF-aligned documentation", "Heat & UV-stabilised grades", "Bio-content reporting"],
        },
        {
          name: "Packaging",
          intro:
            "Food-contact biopolymers and engineered compounds for rigid and flexible packaging. Validated on industrial converters across the EU.",
          bullets: ["EU 10/2011 compliant grades", "Thermoforming & injection", "Compostable options"],
        },
        {
          name: "Consumer Goods",
          intro:
            "Durable consumer products that combine premium tactility with reliable performance — bio-based and wood-fibre compounds engineered for series production.",
          bullets: ["Premium matte surfaces", "Colour-matched series", "Low odour & VOC"],
        },
        {
          name: "Furniture & Interiors",
          intro:
            "Authentic wood-look compounds and natural fibre composites for architectural and furniture applications — material aesthetics with industrial repeatability.",
          bullets: ["Wood content 25–55 %", "Sand-able surfaces", "Indoor air-quality grades"],
        },
        {
          name: "Electrical & Electronics",
          intro:
            "Flame-retarded technical compounds, mineral-filled grades and dimensionally stable formulations for housings, connectors and structural elements.",
          bullets: ["UL-class targeted grades", "Low warpage formulations", "EMC-compatible options"],
        },
        {
          name: "Additive Manufacturing",
          intro:
            "Industrial-grade FDM filaments and pellet AM granulates with consistent rheology and surface finish — supporting prototyping and end-use parts.",
          bullets: ["1.75 / 2.85 mm filament", "Pellet AM granulates", "Engineering & bio grades"],
        },
      ],
    },

    innovation: {
      hero_eyebrow: "Innovation",
      hero_title: "Material science. Industrially engineered.",
      hero_intro:
        "Our laboratories combine polymer chemistry, rheology, thermal analysis and pilot extrusion under a single roof — connecting research with European manufacturing reality.",
      phil_eyebrow: "R&D Philosophy",
      phil_title: "From a molecule to a moulded part.",
      phil_intro:
        "We believe industrial material development is an engineering discipline, not a marketing one. Every compound is characterised at multiple scales — molecular, processing and application — before it enters a customer specification.",
      cap_eyebrow: "Laboratory Capabilities",
      cap_title: "Six disciplines, one development team.",
      method_eyebrow: "Method",
      method_title: "DOE-driven development. Validated for production.",
      method_intro:
        "Statistical Design of Experiments combined with industrial-scale pilot trials produces robust process windows — measured in real converter conditions, not theoretical ones.",
      stats: [
        ["120+", "Active formulations"],
        ["1.4 kt", "Annual capacity"],
        ["48h", "Sample turnaround"],
        ["EU", "Manufactured in Latvia"],
      ],
      capabilities: [
        { title: "Polymer Chemistry", desc: "Bio-based polymer synthesis pathways, reactive compatibilisation and additive design — built around renewable feedstocks." },
        { title: "Rheology & Flow", desc: "Capillary and rotational rheometry, MFI and process-mirror tests to predict converter behaviour before scale-up." },
        { title: "Thermal Analysis", desc: "DSC, TGA and HDT to map crystallisation, degradation and load-bearing thermal performance under real operating windows." },
        { title: "Mechanical Testing", desc: "Tensile, flexural, impact and creep characterisation — calibrated against ISO and ASTM industrial benchmarks." },
        { title: "Microscopy & Surface", desc: "Optical and electron microscopy for dispersion, fibre-matrix interface and failure-mode investigation." },
        { title: "Pilot Engineering", desc: "Twin-screw process engineering and side-stuff design to translate laboratory chemistry into industrial reality." },
      ],
    },

    sustainability: {
      hero_eyebrow: "Sustainability",
      hero_title: "Engineering, not marketing.",
      hero_intro:
        "Sustainability at BALTIVEX is treated as a material engineering discipline — measured, documented and defended with the same rigour as mechanical performance.",
      pillars_eyebrow: "Five Pillars",
      pillars_title_l1: "Material decisions",
      pillars_title_l2: "with measurable impact.",
      pillars_intro:
        "We avoid abstract environmental statements. Every BALTIVEX material decision is backed by data, methodology and traceable feedstock — designed for European customers who must report and defend their own sustainability claims.",
      pillar_label: "Pillar",
      method_eyebrow: "Methodology",
      method_title: "Data over storytelling.",
      method_intro:
        "Carbon, bio-content and end-of-life data are provided as methodology-backed engineering inputs — not marketing assets. Our customers use them to make defensible decisions in audited industrial environments.",
      pillars: [
        { code: "01", title: "Circular Economy", desc: "Compounds engineered for mechanical recycling, reprocessing windows mapped through pilot trials and material end-of-life documented for industrial customers." },
        { code: "02", title: "Renewable Feedstocks", desc: "Bio-based monomers, natural fibres and wood-flour from certified European sources — replacing fossil feedstocks where engineering performance allows." },
        { code: "03", title: "Material Efficiency", desc: "Lightweighting, part consolidation and cycle-time optimisation — reducing the embodied energy of every kilogram of finished product." },
        { code: "04", title: "Lower Carbon Footprint", desc: "Cradle-to-gate carbon reporting on request. Bio-content uplift programmes that demonstrably reduce CO₂-eq per kg of part." },
        { code: "05", title: "Responsible Innovation", desc: "Honest, engineering-based sustainability claims. No greenwashing — only verified data and methodology customers can defend." },
      ],
    },

    about: {
      hero_eyebrow: "About BALTIVEX",
      hero_title: "A European advanced materials company.",
      hero_intro:
        "Founded in Latvia, BALTIVEX engineers and manufactures advanced biopolymer compounds, biocomposites and engineered polymers — supplying manufacturers, converters and product developers across Europe.",
      who_eyebrow: "Who we are",
      who_title_l1: "A materials company,",
      who_title_l2: "built like an engineering firm.",
      who_intro:
        "BALTIVEX is structured around polymer engineers, process technologists and material scientists. We are not a startup, not a distributor, and not an environmental NGO — we are a European materials manufacturer that takes engineering rigour and documentation as seriously as our customers do.",
      principles_eyebrow: "Principles",
      principles_title: "Four principles that shape every formulation.",
      principle_label: "Principle",
      hq_eyebrow: "Headquarters",
      hq_title_l1: "Ogre district, Latvia.",
      hq_title_l2: "European Union.",
      hq_intro:
        "Our manufacturing and R&D are located in Latvia, supplying customers across the EU with full European industrial standards and logistics.",
      principles: [
        ["Engineering first", "Material decisions are made by polymer engineers, not marketers."],
        ["European manufacturing", "Produced in Latvia under EU industrial standards with full traceability."],
        ["Long-term partnerships", "We measure success in decade-long customer relationships, not single orders."],
        ["Documented quality", "Every batch carries CoA, SDS and traceable feedstock documentation."],
      ],
    },

    contact: {
      hero_eyebrow: "Contact",
      hero_title: "Brief our engineering team.",
      hero_intro:
        "Tell us about your application, conversion process and performance targets. Our polymer engineers will respond with a material direction — typically within two working days.",
      hq_eyebrow: "Headquarters",
      hq_company: "BALTIVEX",
      hq_addr: "Ogre district, Latvia · European Union",
      inquiry_eyebrow: "For your inquiry",
      inquiry_items: [
        "· Material datasheets and CoA on request",
        "· Custom formulation briefings",
        "· Pilot trial and scale-up consultations",
        "· Distribution & logistics across the EU",
      ],
      f_name: "Full Name *",
      f_name_ph: "Jane Doe",
      f_email: "Email *",
      f_email_ph: "name@company.eu",
      f_phone: "Phone",
      f_phone_ph: "+371 ...",
      f_company: "Company",
      f_company_ph: "Organisation name",
      f_industry: "Industry",
      f_industry_ph: "Select industry",
      f_interest: "Interest",
      f_interest_ph: "Material / service",
      f_message: "Message *",
      f_message_ph:
        "Describe your application, target conversion process, volumes and performance requirements.",
      privacy:
        "By submitting, you agree your details will be used solely to respond to your inquiry. No marketing emails.",
      submit: "Submit Inquiry",
      submitting: "Sending…",
      success: "Thank you. Our engineering team will respond shortly.",
      err_name: "Please enter your full name.",
      err_email: "Please enter a valid email.",
      err_message: "Please add a short message (10+ characters).",
      err_submit: "Could not submit. Please try again.",
      industry_options: [
        "Automotive",
        "Packaging",
        "Consumer Goods",
        "Furniture & Interiors",
        "Electrical & Electronics",
        "Additive Manufacturing",
        "Other",
      ],
      interest_options: [
        "PLA Compounds",
        "Biocomposites",
        "Wood-filled Polymers",
        "Technical Compounds",
        "Masterbatch Solutions",
        "3D Printing Materials",
        "Custom Formulation",
        "Pilot / Scale-up",
        "Other",
      ],
    },

    notfound: {
      label: "Error · 404",
      title_l1: "This page is not part",
      title_l2_a: "of the BALTIVEX",
      title_l2_b: "portfolio.",
      cta: "Return Home",
    },
  },

  lv: {
    nav: {
      products: "Produkti",
      services: "Pakalpojumi",
      industries: "Nozares",
      innovation: "Inovācijas",
      sustainability: "Ilgtspēja",
      about: "Par mums",
      contact: "Kontakti",
      cta: "Sākt projektu",
    },
    footer: {
      tagline: "Advanced Biomaterials",
      intro:
        "Eiropas uzlaboto materiālu uzņēmums, kas izstrādā biopolimērus, biokompozītus un specializētus savienojumus rūpnieciskajiem ražotājiem visā kontinentā.",
      cta: "Sākt projektu",
      col_materials: "Materiāli",
      col_services: "Pakalpojumi",
      col_company: "Uzņēmums",
      links_materials: [
        "PLA savienojumi",
        "Biokompozīti",
        "Koka pildīti polimēri",
        "Tehniskie savienojumi",
        "Masterbatch risinājumi",
        "3D drukas materiāli",
      ],
      links_services: [
        "Individuāla kompaundēšana",
        "Materiālu izstrāde",
        "Pilotražošana",
        "Tehniskā konsultācija",
        "Rūpnieciskā mēroga ražošana",
      ],
      links_company: [
        "Par BALTIVEX",
        "Inovācijas",
        "Ilgtspēja",
        "Nozares",
        "Kontakti",
      ],
      copyright: "BALTIVEX · Ogres novads, Latvija · Eiropas Savienība",
    },
    home: {
      hero_eyebrow: "Modernie biomateriāli · Latvija · Eiropas Savienība",
      hero_l1: "Inženiertehniski",
      hero_l2: "materiāli Eiropas",
      hero_l3_a: "ražošanai.",
      hero_intro:
        "BALTIVEX izstrādā un ražo biopolimēru savienojumus, biokompozītus un specializētus materiālus visprasīgākajiem rūpnieciskajiem pielietojumiem visā Eiropā.",
      hero_cta1: "Iepazīt materiālus",
      hero_cta2: "Pieprasīt paraugu",
      metrics: [
        ["120+", "Materiālu formulējumi"],
        ["18", "Eiropas partneri"],
        ["1,4 kt", "Gada jauda"],
        ["ES", "Ražots · Latvijā"],
      ],

      products_eyebrow: "Materiālu portfelis",
      products_title: "Augstvērtīgi savienojumi, izstrādāti veiktspējai.",
      products_intro:
        "No PLA biopolimēriem līdz koka pildītiem tehniskajiem savienojumiem — mūsu portfelis ir veidots pārstrādātājiem un ražotājiem, kuriem ir nepieciešama nemainīga kvalitāte rūpnieciskā mērogā.",
      products_all: "Visi materiāli",

      products_grid: [
        "PLA savienojumi",
        "Biokompozīti",
        "Koka pildīti polimēri",
        "Tehniskie savienojumi",
        "Masterbatch risinājumi",
        "3D drukas materiāli",
      ],

      services_eyebrow: "Rūpnieciskie pakalpojumi",
      services_title: "No formulas līdz rūpnieciskajam mērogam.",
      services_intro:
        "Mēs strādājam kopā ar jūsu inženieru un iepirkumu komandām visā materiāla dzīves ciklā — koncepcija, validācija, pilotseriālā un sērijveida ražošana.",
      services_link: "Skatīt iespējas",
      services_list: [
        "Individuāla kompaundēšana",
        "Materiālu izstrāde",
        "Pilotražošana",
        "Tehniskā konsultācija",
        "Pētniecības partnerības",
        "Rūpnieciskā mēroga ražošana",
      ],

      industries_eyebrow: "Nozares",
      industries_title: "Uzticams nozarēs, kurās nepieciešama precizitāte.",
      industries_intro:
        "Mūsu materiāli darbina liela apjoma iepakojuma līnijas, autointerjeru detaļas, patēriņa preces un arhitektūras elementus visā Eiropas Savienībā.",
      industries_grid: [
        "Auto rūpniecība",
        "Iepakojums",
        "Patēriņa preces",
        "Mēbeles un interjers",
      ],
      industries_industry: "Nozare",
      industries_explore: "Iepazīt",

      innovation_eyebrow: "Inovācijas",
      innovation_title_l1: "Pētniecība, balstīta",
      innovation_title_l2: "materiālu zinātnē.",
      innovation_intro:
        "Mūsu laboratorijas apvieno polimēru ķīmiju, reoloģiju un termisko analīzi ar rūpnieciska līmeņa pilotlīnijām — savienojot zinātni ar sērijveida ražošanu.",
      innovation_link: "Mūsu laboratorija",
      innovation_labs: [
        ["DSC", "Termiskā analīze"],
        ["MFI", "Kausējuma plūsma"],
        ["TGA", "Sadalīšanās"],
        ["SEM", "Mikroskopija"],
      ],

      why_eyebrow: "Kāpēc BALTIVEX",
      why_title: "Inženierija, ko var droši norādīt specifikācijā.",
      why_cards: [
        {
          t: "Eiropas ražošana",
          d: "Ražots Latvijā saskaņā ar ES rūpnieciskajiem standartiem, ar pilnīgu izsekojamību, partijas dokumentāciju un CoA katrai piegādei.",
        },
        {
          t: "Materiālu inženierija",
          d: "Individuāli formulējumi, ko izstrādā polimēru inženieri — pielāgoti jūsu pārstrādes iekārtai, instrumentam un detaļai.",
        },
        {
          t: "No pilota līdz sērijai",
          d: "Validētas pilotlīnijas savieno laboratorijas izstrādi ar uzticamu un atkārtojamu rūpniecisku ražošanu.",
        },
        {
          t: "Atbildīga inovācija",
          d: "Biopolimēri un dabīgo šķiedru ķīmijas, kas samazina fosilo izejvielu atkarību, nezaudējot veiktspēju.",
        },
      ],

      cta_eyebrow: "Sāciet projektu",
      cta_title_l1: "No jūsu specifikācijas",
      cta_title_l2: "līdz validētam materiālam —",
      cta_title_l3: "kopā.",
      cta_button: "Pieprasīt materiālu īsumā",
    },

    products: {
      hero_eyebrow: "Materiālu portfelis",
      hero_title: "Rūpnieciski izstrādāti savienojumi.",
      hero_intro:
        "Pilns biopolimēru, biokompozītu un tehnisko savienojumu portfelis — izstrādāts pārstrādātājiem, OEM un produktu izstrādātājiem visā Eiropā.",
      group_label: "Grupa",
      bottom_eyebrow: "Datu lapas · CoA · Paraugi",
      bottom_title_l1: "Pastāstiet, ko ražojat.",
      bottom_title_l2: "Mēs izstrādāsim materiālu.",
      bottom_cta: "Sākt projektu",
      groups: [
        {
          name: "PLA savienojumi",
          tagline: "Biobāzēti inženierijas materiāli",
          description:
            "Polilaktīdskābes savienojumi termoveidošanai, smidzināšanas liešanai un ekstrūzijai. Pielāgota kristalitāte, triecienizturība un termiska deformācija pēc pieprasījuma.",
          specs: ["Biobāzes saturs > 85%", "Tg 55–62 °C", "MFI 4–24 g/10 min"],
        },
        {
          name: "Biokompozīti",
          tagline: "Ar dabīgām šķiedrām pastiprināti polimēri",
          description:
            "Ar liniem, kaņepēm un celulozi pastiprināti savienojumi ar inženiertehnisku šķiedras–matricas saķeri. Izstrādāti stingrībai, zemam blīvumam un akustiskai amortizācijai.",
          specs: ["Blīvums 1,05–1,18 g/cm³", "Lieces modulis līdz 6,2 GPa", "Šķiedras 20–40 %"],
        },
        {
          name: "Koka pildīti polimēri",
          tagline: "Dabīga virsma, tehniska veiktspēja",
          description:
            "Ar koka miltiem un skuju koka šķiedrām pildīti savienojumi mēbeļu, interjera un patēriņa lietojumiem. Autentiska sajūta un matēta apdare ar rūpniecisku atkārtojamību.",
          specs: ["Koka saturs 25–55 %", "PP, PE, PLA matricas", "Ērti instrumentiem"],
        },
        {
          name: "Tehniskie savienojumi",
          tagline: "Pastiprināti ar minerāliem un stiklu",
          description:
            "Augstas veiktspējas pastiprināti savienojumi autorūpniecībai, elektrotehnikai un konstrukcijām. Šaura tolerance kausējuma plūsmai un saraušanai.",
          specs: ["Stikls / Talks / CaCO₃", "Līdz 50 % pastiprinājums", "Termiski stabilizēti materiāli"],
        },
        {
          name: "Masterbatch risinājumi",
          tagline: "Krāsas, piedevas un funkcionālie",
          description:
            "Individuāli krāsu, UV, antistatikas, slīdēšanas, anti-bloķēšanas un apstrādes palīgvielu masterbatch risinājumi, kas pielāgoti jūsu nesējam, līnijai un detaļai.",
          specs: ["Pantone un RAL toņi", "Pārtikas kontaktam atbilstoši", "UV / IR / NIR varianti"],
        },
        {
          name: "3D drukas materiāli",
          tagline: "Rūpnieciskas kvalitātes pavedieni un granulas",
          description:
            "FDM pavedieni un granulu bāzes materiāli rūpnieciskajai aditīvai ražošanai. Biobāzes, šķiedru pildīti un inženierijas materiāli prototipiem un gala detaļām.",
          specs: ["1,75 / 2,85 mm pavediens", "AM granulas", "Koks, lini un PLA-X"],
        },
        {
          name: "Individuāli formulējumi",
          tagline: "Izstrādāti pēc jūsu specifikācijas",
          description:
            "Pielāgoti savienojumi, kas izstrādāti pēc jūsu veiktspējas prasībām — no izejvielām un piedevu dizaina līdz validētai rūpnieciskai ražošanai.",
          specs: ["NDA aizsargāta pētniecība", "DOE balstīta izstrāde", "Pilna CoA un izsekojamība"],
        },
      ],
    },

    services: {
      hero_eyebrow: "Rūpnieciskie pakalpojumi",
      hero_title: "Materiālu partneri rūpnieciskajai ražošanai.",
      hero_intro:
        "Mēs strādājam ar jūsu inženieru, iepirkumu un ražošanas komandām visā savienojuma dzīves ciklā — no veiktspējas prasībām līdz pilnībā validētam rūpnieciskam materiālam.",
      sidebar_eyebrow: "Iespējas",
      sidebar_title_l1: "Septiņas iespējas,",
      sidebar_title_l2: "viens inženiertehnisks partneris.",
      sidebar_intro:
        "Mūsu komanda sastāv no polimēru inženieriem, procesu tehnologiem un materiālu zinātniekiem — atbalstot jūsu projektu no pirmā kilograma līdz pirmajam konteineram.",
      sidebar_cta: "Sazināties ar mūsu inženieriem",
      process_eyebrow: "Process",
      process_title: "Brīfs · Izstrāde · Pilots · Validācija · Ražošana.",
      process_intro:
        "Atkārtojams inženiertehnisks process, kas samazina laiku līdz tirgum un noņem risku no materiāla ieviešanas — paredzēts Eiropas rūpnieciskajiem klientiem ar stingrām dokumentācijas prasībām.",
      items: [
        {
          title: "Individuāla kompaundēšana",
          summary:
            "Individuāla savienojumu izstrāde uz dvīņskrūves ekstrūzijas līnijām, kas pielāgotas jūsu pārstrādātājam un galīgajam pielietojumam.",
          points: [
            "Dvīņskrūves līdzgriezīga ekstrūzija, 27–70 mm",
            "Sānu padeve šķiedrām un jutīgām piedevām",
            "In-line gravimetriska dozēšana un vakuuma degazēšana",
          ],
        },
        {
          title: "Materiālu izstrāde",
          summary:
            "Polimēru inženieri pārvērš jūsu veiktspējas prasības validētā un mērogojamā materiālā — ar pilnu laboratorijas raksturojumu.",
          points: ["DOE balstīta formulēšana", "Atteices režīma un atkārtojamības pētījums", "Datu lapa + CoA + SDS"],
        },
        {
          title: "Pilotražošana",
          summary:
            "Savienojam laboratorijas darbu ar rūpniecisko realitāti uz validētām pilotlīnijām, kas atveido jūsu pārstrādes apstākļus.",
          points: ["25–500 kg izmēģinājuma partijas", "Procesa logu un reoloģijas kartēšana", "Žāvēšana un dozēšana"],
        },
        {
          title: "Tehniskā konsultācija",
          summary:
            "Iesaistīts atbalsts materiālu izvēlē, iekārtu konfigurācijā un pārstrādē — no prototipa līdz sērijai.",
          points: ["Materiālu izvēles darbnīcas", "Pārstrādes problēmu risināšana", "Izmaksu inženierijas auditi"],
        },
        {
          title: "Pētniecība un izstrāde",
          summary:
            "Ilgtermiņa P&I partnerības biobāzes ķīmijā, šķiedru pastiprināšanā un funkcionālajos masterbatch risinājumos.",
          points: ["NDA aizsargāta sadarbība", "Horizon tipa kopīga pētniecība", "IP tīri izstrādes ceļi"],
        },
        {
          title: "Materiālu optimizācija",
          summary:
            "Esošo savienojumu pārveidošana, lai samazinātu izmaksas, uzlabotu ilgtspējas profilu vai atvērtu jaunus pārstrādes logus.",
          points: ["Biobāzes satura palielināšana", "Cikla laika samazināšana", "Vieglināšana un detaļu konsolidācija"],
        },
        {
          title: "Rūpnieciskā mēroga ražošana",
          summary:
            "Pāreja no validēta pilota uz atkārtojamu sērijveida ražošanu ar partijas izsekojamību, CoA un ES atbilstīgu dokumentāciju.",
          points: ["1,4 kt gada jauda", "Daudzmaiņu ražošana", "Eiropas loģistikas tīkls"],
        },
      ],
    },

    industries: {
      hero_eyebrow: "Nozares, kurām kalpojam",
      hero_title: "Materiāli nozarēm, kas pieprasa precizitāti.",
      hero_intro:
        "No autointerjeriem līdz rūpnieciskajam iepakojumam un arhitektūras virsmām — BALTIVEX materiālus izvēlas Eiropas ražotāji, kas kvalitāti mēra mikronos un gadu desmitos.",
      sector_label: "Nozare",
      bottom_eyebrow: "Starpnozaru ekspertīze",
      bottom_title: "Viena materiālu platforma. Daudzi rūpnieciski rezultāti.",
      items: [
        {
          name: "Auto rūpniecība",
          intro:
            "Interjera detaļas, motora nodalījuma komponentes un konstrukciju daļas. Biopastiprināti savienojumi, kas samazina svaru un CO₂, nezaudējot veiktspēju.",
          bullets: ["IATF atbilstoša dokumentācija", "Termiski un UV stabilizēti", "Biobāzes satura atskaites"],
        },
        {
          name: "Iepakojums",
          intro:
            "Pārtikas kontaktam paredzēti biopolimēri un specializēti savienojumi cietajam un elastīgajam iepakojumam. Validēti uz ES pārstrādātāju iekārtām.",
          bullets: ["ES 10/2011 atbilstoši", "Termoveidošana un liešana", "Kompostējamas iespējas"],
        },
        {
          name: "Patēriņa preces",
          intro:
            "Izturīgas patēriņa preces, kas apvieno premium taustīšanas sajūtu ar uzticamu veiktspēju — biobāzes un koka šķiedru savienojumi sērijveida ražošanai.",
          bullets: ["Premium matētas virsmas", "Krāsu atbilstība sērijai", "Zems aromāts un GOS"],
        },
        {
          name: "Mēbeles un interjers",
          intro:
            "Autentiski koka izskata savienojumi un dabīgo šķiedru kompozīti arhitektūras un mēbeļu pielietojumiem — materiāla estētika ar rūpniecisku atkārtojamību.",
          bullets: ["Koka saturs 25–55 %", "Slīpējamas virsmas", "Iekštelpu gaisa kvalitāte"],
        },
        {
          name: "Elektrotehnika un elektronika",
          intro:
            "Ugunsdrošības savienojumi, minerālu pildīti un dimensionāli stabili formulējumi korpusiem, savienotājiem un konstrukciju elementiem.",
          bullets: ["UL klases materiāli", "Mazas deformācijas formulas", "EMC saderīgas opcijas"],
        },
        {
          name: "Aditīvā ražošana",
          intro:
            "Rūpnieciskas kvalitātes FDM pavedieni un granulas ar stabilu reoloģiju un virsmas apdari — prototipiem un gala detaļām.",
          bullets: ["1,75 / 2,85 mm pavediens", "AM granulas", "Inženierijas un biobāzes materiāli"],
        },
      ],
    },

    innovation: {
      hero_eyebrow: "Inovācijas",
      hero_title: "Materiālu zinātne. Rūpnieciski izstrādāta.",
      hero_intro:
        "Mūsu laboratorijas apvieno polimēru ķīmiju, reoloģiju, termisko analīzi un pilotekstrūziju zem viena jumta — savienojot pētniecību ar Eiropas ražošanas realitāti.",
      phil_eyebrow: "P&I filozofija",
      phil_title: "No molekulas līdz liešanai.",
      phil_intro:
        "Mēs uzskatām, ka rūpniecisko materiālu izstrāde ir inženiertehniska disciplīna, nevis mārketinga. Katrs savienojums tiek raksturots vairākos līmeņos — molekulārā, procesa un pielietojuma — pirms tas nonāk klienta specifikācijā.",
      cap_eyebrow: "Laboratorijas iespējas",
      cap_title: "Sešas disciplīnas, viena izstrādes komanda.",
      method_eyebrow: "Metode",
      method_title: "DOE balstīta izstrāde. Validēta ražošanai.",
      method_intro:
        "Statistiskā eksperimentu plānošana apvienojumā ar rūpnieciskā mēroga pilotmēģinājumiem rada stabilus procesa logus — mērīti reālos pārstrādes apstākļos, nevis teorētiskos.",
      stats: [
        ["120+", "Aktīvi formulējumi"],
        ["1,4 kt", "Gada jauda"],
        ["48h", "Parauga izpildes laiks"],
        ["ES", "Ražots Latvijā"],
      ],
      capabilities: [
        { title: "Polimēru ķīmija", desc: "Biobāzes polimēru sintēzes ceļi, reaktīvā saderināšana un piedevu dizains — balstīti uz atjaunojamām izejvielām." },
        { title: "Reoloģija un plūsma", desc: "Kapilāra un rotācijas reometrija, MFI un procesa imitācijas testi, lai paredzētu pārstrādātāja uzvedību pirms mēroga palielināšanas." },
        { title: "Termiskā analīze", desc: "DSC, TGA un HDT, lai kartētu kristalizāciju, sadalīšanos un slodzes termisko veiktspēju reālos darba apstākļos." },
        { title: "Mehāniskās pārbaudes", desc: "Stiepes, lieces, trieciena un plūsmas raksturojums — kalibrēts pret ISO un ASTM rūpnieciskajiem etaloniem." },
        { title: "Mikroskopija un virsma", desc: "Optiskā un elektronu mikroskopija dispersijas, šķiedras–matricas saskarnes un atteices režīma pētījumiem." },
        { title: "Pilotinženierija", desc: "Dvīņskrūves procesa inženierija un sānu padeves dizains, lai laboratorijas ķīmiju pārvērstu rūpnieciskā realitātē." },
      ],
    },

    sustainability: {
      hero_eyebrow: "Ilgtspēja",
      hero_title: "Inženierija, nevis mārketings.",
      hero_intro:
        "Ilgtspēja BALTIVEX tiek uzskatīta par materiālu inženierijas disciplīnu — izmērīta, dokumentēta un aizstāvēta ar tādu pašu stingrību kā mehāniskā veiktspēja.",
      pillars_eyebrow: "Pieci balsti",
      pillars_title_l1: "Materiālu lēmumi",
      pillars_title_l2: "ar izmērāmu ietekmi.",
      pillars_intro:
        "Mēs izvairāmies no abstrakta vides apgalvojumiem. Katrs BALTIVEX materiāla lēmums balstās uz datiem, metodoloģiju un izsekojamām izejvielām — paredzēts Eiropas klientiem, kuriem jāatskaitās un jāaizstāv savas ilgtspējas prasības.",
      pillar_label: "Balsts",
      method_eyebrow: "Metodoloģija",
      method_title: "Dati pirms stāstiem.",
      method_intro:
        "Oglekļa, biobāzes satura un dzīves cikla beigu dati tiek sniegti kā metodoloģijā balstīti inženiertehniski ieguldījumi — nevis mārketinga materiāli. Mūsu klienti tos izmanto, lai pieņemtu aizstāvamus lēmumus rūpniecības auditētajos apstākļos.",
      pillars: [
        { code: "01", title: "Aprites ekonomika", desc: "Savienojumi, kas paredzēti mehāniskai pārstrādei, pārstrādes logi, kas kartēti caur pilotmēģinājumiem, un dokumentēts materiāla dzīves cikla beigu posms." },
        { code: "02", title: "Atjaunojamas izejvielas", desc: "Biobāzes monomēri, dabīgās šķiedras un koka milti no sertificētiem Eiropas avotiem — aizstājot fosilās izejvielas, kur inženierijas veiktspēja to atļauj." },
        { code: "03", title: "Materiālu efektivitāte", desc: "Vieglināšana, detaļu konsolidācija un cikla laika optimizācija — samazinot iemiesoto enerģiju katram kilogramam gala produkta." },
        { code: "04", title: "Mazāks oglekļa pēdas nospiedums", desc: "Atskaites par oglekli no šūpuļa līdz vārtiem pēc pieprasījuma. Biobāzes satura palielināšanas programmas, kas pārliecinoši samazina CO₂-eq uz kg detaļas." },
        { code: "05", title: "Atbildīga inovācija", desc: "Godīgi, uz inženieriju balstīti ilgtspējas apgalvojumi. Nekāda zaļmazgāšanās — tikai pārbaudīti dati un metodoloģija, ko klienti var aizstāvēt." },
      ],
    },

    about: {
      hero_eyebrow: "Par BALTIVEX",
      hero_title: "Eiropas uzlaboto materiālu uzņēmums.",
      hero_intro:
        "Dibināts Latvijā, BALTIVEX projektē un ražo modernos biopolimēru savienojumus, biokompozītus un specializētus polimērus — apgādājot ražotājus, pārstrādātājus un produktu izstrādātājus visā Eiropā.",
      who_eyebrow: "Kas mēs esam",
      who_title_l1: "Materiālu uzņēmums,",
      who_title_l2: "veidots kā inženieru birojs.",
      who_intro:
        "BALTIVEX ir veidots ap polimēru inženieriem, procesu tehnologiem un materiālu zinātniekiem. Mēs neesam starta kompānija, neesam izplatītājs un neesam vides NVO — mēs esam Eiropas materiālu ražotājs, kas inženieru stingrību un dokumentāciju uztver tikpat nopietni kā mūsu klienti.",
      principles_eyebrow: "Principi",
      principles_title: "Četri principi, kas veido katru formulu.",
      principle_label: "Princips",
      hq_eyebrow: "Galvenā mītne",
      hq_title_l1: "Ogres novads, Latvija.",
      hq_title_l2: "Eiropas Savienība.",
      hq_intro:
        "Mūsu ražošana un P&I atrodas Latvijā, apgādājot klientus visā ES ar pilniem Eiropas rūpnieciskajiem standartiem un loģistiku.",
      principles: [
        ["Inženierija vispirms", "Materiālu lēmumus pieņem polimēru inženieri, nevis mārketinga komanda."],
        ["Eiropas ražošana", "Ražots Latvijā saskaņā ar ES rūpnieciskajiem standartiem un pilnīgu izsekojamību."],
        ["Ilgtermiņa partnerības", "Mēs mērām panākumus desmitgades ilgās klientu attiecībās, nevis vienreizējos pasūtījumos."],
        ["Dokumentēta kvalitāte", "Katrai partijai ir CoA, SDS un izsekojama izejvielu dokumentācija."],
      ],
    },

    contact: {
      hero_eyebrow: "Kontakti",
      hero_title: "Sazinieties ar mūsu inženierijas komandu.",
      hero_intro:
        "Pastāstiet par savu pielietojumu, pārstrādes procesu un veiktspējas mērķiem. Mūsu polimēru inženieri atbildēs ar materiāla virzienu — parasti divu darba dienu laikā.",
      hq_eyebrow: "Galvenā mītne",
      hq_company: "BALTIVEX",
      hq_addr: "Ogres novads, Latvija · Eiropas Savienība",
      inquiry_eyebrow: "Jūsu pieprasījumam",
      inquiry_items: [
        "· Materiālu datu lapas un CoA pēc pieprasījuma",
        "· Individuālu formulu apspriedes",
        "· Pilotmēģinājumu un mēroga palielināšanas konsultācijas",
        "· Izplatīšana un loģistika visā ES",
      ],
      f_name: "Pilns vārds *",
      f_name_ph: "Vārds Uzvārds",
      f_email: "E-pasts *",
      f_email_ph: "vards@uznemums.lv",
      f_phone: "Tālrunis",
      f_phone_ph: "+371 ...",
      f_company: "Uzņēmums",
      f_company_ph: "Organizācijas nosaukums",
      f_industry: "Nozare",
      f_industry_ph: "Izvēlieties nozari",
      f_interest: "Interese",
      f_interest_ph: "Materiāls / pakalpojums",
      f_message: "Ziņojums *",
      f_message_ph:
        "Aprakstiet savu pielietojumu, mērķa pārstrādes procesu, apjomus un veiktspējas prasības.",
      privacy:
        "Iesniedzot, jūs piekrītat, ka jūsu dati tiks izmantoti tikai jūsu pieprasījuma apstrādei. Bez mārketinga vēstulēm.",
      submit: "Iesniegt pieprasījumu",
      submitting: "Sūta…",
      success: "Paldies. Mūsu inženieru komanda atbildēs drīzumā.",
      err_name: "Lūdzu, ievadiet savu pilnu vārdu.",
      err_email: "Lūdzu, ievadiet derīgu e-pastu.",
      err_message: "Lūdzu, pievienojiet īsu ziņojumu (vismaz 10 rakstzīmes).",
      err_submit: "Neizdevās iesniegt. Lūdzu, mēģiniet vēlreiz.",
      industry_options: [
        "Auto rūpniecība",
        "Iepakojums",
        "Patēriņa preces",
        "Mēbeles un interjers",
        "Elektrotehnika un elektronika",
        "Aditīvā ražošana",
        "Cits",
      ],
      interest_options: [
        "PLA savienojumi",
        "Biokompozīti",
        "Koka pildīti polimēri",
        "Tehniskie savienojumi",
        "Masterbatch risinājumi",
        "3D drukas materiāli",
        "Individuāla formula",
        "Pilots / mērogs",
        "Cits",
      ],
    },

    notfound: {
      label: "Kļūda · 404",
      title_l1: "Šī lapa neietilpst",
      title_l2_a: "BALTIVEX",
      title_l2_b: "portfelī.",
      cta: "Atgriezties sākumā",
    },
  },

  de: {
    nav: {
      products: "Produkte",
      services: "Leistungen",
      industries: "Branchen",
      innovation: "Innovation",
      sustainability: "Nachhaltigkeit",
      about: "Über uns",
      contact: "Kontakt",
      cta: "Projekt starten",
    },
    footer: {
      tagline: "Advanced Biomaterials",
      intro:
        "Europäisches Unternehmen für fortschrittliche Werkstoffe — Entwicklung von Biopolymeren, Biokompositen und technischen Compounds für industrielle Hersteller in ganz Europa.",
      cta: "Projekt starten",
      col_materials: "Materialien",
      col_services: "Leistungen",
      col_company: "Unternehmen",
      links_materials: [
        "PLA-Compounds",
        "Biokomposite",
        "Holzgefüllte Polymere",
        "Technische Compounds",
        "Masterbatch-Lösungen",
        "3D-Druck-Materialien",
      ],
      links_services: [
        "Kunden­spezifisches Compoundieren",
        "Material­entwicklung",
        "Pilotproduktion",
        "Technische Beratung",
        "Industrielles Scale-up",
      ],
      links_company: [
        "Über BALTIVEX",
        "Innovation",
        "Nachhaltigkeit",
        "Branchen",
        "Kontakt",
      ],
      copyright: "BALTIVEX · Bezirk Ogre, Lettland · Europäische Union",
    },
    home: {
      hero_eyebrow: "Fortschrittliche Biomaterialien · Lettland · Europäische Union",
      hero_l1: "Technische Werkstoffe",
      hero_l2: "für die europäische",
      hero_l3_a: "Fertigung.",
      hero_intro:
        "BALTIVEX entwickelt und fertigt Biopolymer-Compounds, Biokomposite und technische Formulierungen für die anspruchsvollsten industriellen Anwendungen in ganz Europa.",
      hero_cta1: "Materialien entdecken",
      hero_cta2: "Muster anfragen",
      metrics: [
        ["120+", "Material­formulierungen"],
        ["18", "Europäische Partner"],
        ["1,4 kt", "Jahres­kapazität"],
        ["EU", "Hergestellt · Lettland"],
      ],

      products_eyebrow: "Material­portfolio",
      products_title: "Premium-Compounds, technisch perfektioniert.",
      products_intro:
        "Von PLA-Biopolymeren bis zu holzgefüllten technischen Compounds — unser Portfolio ist für Verarbeiter und Hersteller entwickelt, die Konsistenz im industriellen Maßstab erwarten.",
      products_all: "Alle Materialien",

      products_grid: [
        "PLA-Compounds",
        "Biokomposite",
        "Holzgefüllte Polymere",
        "Technische Compounds",
        "Masterbatch-Lösungen",
        "3D-Druck-Materialien",
      ],

      services_eyebrow: "Industrielle Leistungen",
      services_title: "Von der Formulierung zur Serienproduktion.",
      services_intro:
        "Wir begleiten Ihre Engineering- und Einkaufsteams über den gesamten Material-Lebenszyklus — Konzept, Validierung, Pilot- und Serien­produktion.",
      services_link: "Leistungen ansehen",
      services_list: [
        "Kundenspezifisches Compoundieren",
        "Material­entwicklung",
        "Pilotproduktion",
        "Technische Beratung",
        "F&E-Partnerschaften",
        "Industrielles Scale-up",
      ],

      industries_eyebrow: "Branchen",
      industries_title: "Vertraut in Branchen, die Präzision verlangen.",
      industries_intro:
        "Unsere Materialien treiben Hochvolumen-Verpackungs­linien, Automobil-Innenraumteile, Konsumgüter und Architektur­elemente in der gesamten Europäischen Union an.",
      industries_grid: [
        "Automobil",
        "Verpackung",
        "Konsumgüter",
        "Möbel & Interieur",
      ],
      industries_industry: "Branche",
      industries_explore: "Mehr erfahren",

      innovation_eyebrow: "Innovation",
      innovation_title_l1: "F&E verwurzelt in",
      innovation_title_l2: "Material­wissenschaft.",
      innovation_intro:
        "Unsere Labore vereinen Polymerchemie, Rheologie und Thermoanalyse mit industrietauglichen Pilotlinien — sie schließen die Lücke zwischen Forschung und Serien­produktion.",
      innovation_link: "Einblick ins Labor",
      innovation_labs: [
        ["DSC", "Thermoanalyse"],
        ["MFI", "Schmelzfluss­index"],
        ["TGA", "Zersetzung"],
        ["SEM", "Mikroskopie"],
      ],

      why_eyebrow: "Warum BALTIVEX",
      why_title: "Engineering, das Sie mit Vertrauen spezifizieren können.",
      why_cards: [
        {
          t: "Europäische Fertigung",
          d: "Hergestellt in Lettland nach EU-Industrie­standards mit vollständiger Rückverfolgbarkeit, Chargen­dokumentation und CoA bei jeder Lieferung.",
        },
        {
          t: "Material-Engineering",
          d: "Maßgeschneiderte Formulierungen, entwickelt von Polymer­ingenieuren — abgestimmt auf Ihre Maschine, Ihr Werkzeug und Ihr Bauteil.",
        },
        {
          t: "Vom Pilot zur Serie",
          d: "Validierte Pilotlinien verbinden Laborentwicklung mit zuverlässiger, wiederholbarer industrieller Produktion.",
        },
        {
          t: "Verantwortungs­volle Innovation",
          d: "Biopolymer- und Naturfaser-Chemien, die die Abhängigkeit von fossilen Rohstoffen reduzieren — ohne Kompromisse bei der Leistung.",
        },
      ],

      cta_eyebrow: "Starten Sie Ihr Projekt",
      cta_title_l1: "Von Ihrer Spezifikation",
      cta_title_l2: "zum validierten Material —",
      cta_title_l3: "gemeinsam.",
      cta_button: "Material-Briefing anfragen",
    },

    products: {
      hero_eyebrow: "Material­portfolio",
      hero_title: "Compounds für industrielle Leistung.",
      hero_intro:
        "Ein vollständiges Portfolio an Biopolymer-, Biokomposit- und technischen Compounds — entwickelt für Verarbeiter, OEM und Produktentwickler in ganz Europa.",
      group_label: "Gruppe",
      bottom_eyebrow: "Datenblätter · CoA · Muster",
      bottom_title_l1: "Sagen Sie uns, was Sie herstellen.",
      bottom_title_l2: "Wir entwickeln das Material.",
      bottom_cta: "Projekt starten",
      groups: [
        {
          name: "PLA-Compounds",
          tagline: "Biobasierte technische Typen",
          description:
            "Polymilchsäure-Compounds für Thermoformen, Spritzguss und Extrusion. Angepasste Kristallinität, Schlagzähigkeits­modifikation und Wärmeform­beständigkeit auf Anfrage.",
          specs: ["Biobasierter Anteil > 85 %", "Tg 55–62 °C", "MFI 4–24 g/10 min"],
        },
        {
          name: "Biokomposite",
          tagline: "Mit Naturfasern verstärkte Polymere",
          description:
            "Mit Flachs, Hanf und Cellulose verstärkte Compounds mit optimierter Faser-Matrix-Anbindung. Entwickelt für Steifigkeit, geringe Dichte und akustische Dämpfung.",
          specs: ["Dichte 1,05–1,18 g/cm³", "Biegemodul bis 6,2 GPa", "Faser 20–40 %"],
        },
        {
          name: "Holzgefüllte Polymere",
          tagline: "Natürliche Oberfläche, technische Leistung",
          description:
            "Mit Holzmehl und Weichholzfasern gefüllte Compounds für Möbel-, Interieur- und Konsum­anwendungen. Authentische Haptik und matte Oberfläche mit industrieller Reproduzierbarkeit.",
          specs: ["Holzanteil 25–55 %", "PP-, PE-, PLA-Matrizen", "Werkzeug­schonend"],
        },
        {
          name: "Technische Compounds",
          tagline: "Mineral- und glasfaser­verstärkt",
          description:
            "Hochleistungs-Compounds für Automobil-, Elektro- und Konstruktions­anwendungen. Enge Toleranzen bei Schmelzfluss und Schwindung.",
          specs: ["Glas / Talk / CaCO₃", "Bis zu 50 % Verstärkung", "Wärmestabilisierte Typen"],
        },
        {
          name: "Masterbatch-Lösungen",
          tagline: "Farb-, Additiv- und Funktions­masterbatches",
          description:
            "Kundenspezifische Farb-, UV-, Antistatik-, Slip-, Anti-Block- und Verarbeitungs­hilfsmittel-Masterbatches, abgestimmt auf Ihr Trägermaterial, Ihre Linie und Ihr Bauteil.",
          specs: ["Pantone & RAL Farbabstimmung", "Lebensmittel­kontakt-Optionen", "UV / IR / NIR Typen"],
        },
        {
          name: "3D-Druck-Materialien",
          tagline: "Filamente und Granulate in Industrie­qualität",
          description:
            "FDM-Filamente und pelletbasierte Granulate für die industrielle additive Fertigung. Biobasierte, faserverstärkte und technische Typen für Prototypen und Serienteile.",
          specs: ["1,75 / 2,85 mm Filament", "AM-Pellet-Granulate", "Holz, Flachs & PLA-X"],
        },
        {
          name: "Kundenspezifische Formulierungen",
          tagline: "Nach Ihrer Spezifikation entwickelt",
          description:
            "Maßgeschneiderte Compounds, entwickelt aus Ihrem Leistungs-Briefing — von der Rohstoffauswahl und Additiv­auslegung bis zur validierten industriellen Serienproduktion.",
          specs: ["NDA-geschützte F&E", "DOE-basierte Entwicklung", "Vollständige CoA & Rück­verfolgbarkeit"],
        },
      ],
    },

    services: {
      hero_eyebrow: "Industrielle Leistungen",
      hero_title: "Material­partner für die industrielle Fertigung.",
      hero_intro:
        "Wir arbeiten mit Ihren Engineering-, Einkaufs- und Betriebsteams über den gesamten Lebenszyklus eines Compounds — von der Leistungs­anforderung bis zum vollständig validierten Industrie­material.",
      sidebar_eyebrow: "Kompetenzen",
      sidebar_title_l1: "Sieben Kompetenzen,",
      sidebar_title_l2: "ein Engineering-Partner.",
      sidebar_intro:
        "Unser Team besteht aus Polymer­ingenieuren, Prozess­technologen und Material­wissenschaftlern — wir begleiten Ihr Projekt vom ersten Kilogramm bis zum ersten Container.",
      sidebar_cta: "Unsere Ingenieure briefen",
      process_eyebrow: "Prozess",
      process_title: "Briefing · Entwicklung · Pilot · Validierung · Produktion.",
      process_intro:
        "Ein wiederholbarer Engineering-Prozess, der die Time-to-Market verkürzt und das Risiko der Materialeinführung minimiert — entwickelt für europäische Industriekunden mit strengen Dokumentations­anforderungen.",
      items: [
        {
          title: "Kundenspezifisches Compoundieren",
          summary:
            "Individuelle Compound-Entwicklung auf Doppelschnecken-Extrusionslinien, abgestimmt auf Ihren Verarbeiter und Ihre Endanwendung.",
          points: [
            "Gleichläufige Doppelschnecken-Extrusion, 27–70 mm",
            "Seitenein­bringung für Fasern und sensible Additive",
            "Inline-Gravimetrie & Vakuum-Entgasung",
          ],
        },
        {
          title: "Material­entwicklung",
          summary:
            "Polymer­ingenieure übersetzen Ihr Leistungs-Briefing in ein validiertes, skalierbares Material — abgesichert durch vollständige Labor­charakterisierung.",
          points: ["DOE-basierte Formulierung", "Fehlermodus- und Reproduzierbarkeits­studie", "Datenblatt + CoA + SDS"],
        },
        {
          title: "Pilotproduktion",
          summary:
            "Verbindung zwischen Laborarbeit und industrieller Realität auf validierten Pilotlinien, die Ihre nachgelagerten Prozess­bedingungen replizieren.",
          points: ["25–500 kg Versuchschargen", "Prozess­fenster & Rheologie-Mapping", "Werkzeug­schonende Trocknung & Dosierung"],
        },
        {
          title: "Technische Beratung",
          summary:
            "Eingebettete Unterstützung bei Materialauswahl, Maschinen­einrichtung und nachgelagerter Verarbeitung — vom Prototyp zur Serie.",
          points: ["Materialauswahl-Workshops", "Verarbeitungs­problem-Lösungen", "Kosten-Engineering-Reviews"],
        },
        {
          title: "Forschung & Entwicklung",
          summary:
            "Langfristige F&E-Partnerschaften in biobasierter Chemie, Faserverstärkung und funktionalen Masterbatches.",
          points: ["NDA-geschützte Zusammenarbeit", "Horizon-Style-Verbundforschung", "IP-saubere Entwicklungs­pfade"],
        },
        {
          title: "Material­optimierung",
          summary:
            "Neukonzeption bestehender Compounds zur Kostensenkung, Verbesserung des Nachhaltigkeits­profils oder Erschließung neuer Verarbeitungs­fenster.",
          points: ["Erhöhung des Biobasis-Anteils", "Reduzierung der Zykluszeit", "Leichtbau & Teile­konsolidierung"],
        },
        {
          title: "Industrielles Scale-up",
          summary:
            "Übergang vom validierten Pilot zur wiederholbaren Serien­produktion mit Chargen­rückverfolgung, CoA und EU-konformer Dokumentation.",
          points: ["1,4 kt Jahres­kapazität", "Mehrschicht-Produktion", "Europaweites Logistik­netzwerk"],
        },
      ],
    },

    industries: {
      hero_eyebrow: "Branchen, die wir bedienen",
      hero_title: "Materialien für Branchen, die Präzision verlangen.",
      hero_intro:
        "Vom Automobil-Innenraum über industrielle Verpackungen bis hin zu Architektur­oberflächen — BALTIVEX-Materialien werden von europäischen Herstellern spezifiziert, die Qualität in Mikrometern und Jahrzehnten messen.",
      sector_label: "Sektor",
      bottom_eyebrow: "Branchen­übergreifende Expertise",
      bottom_title: "Eine Material­plattform. Viele industrielle Ergebnisse.",
      items: [
        {
          name: "Automobil",
          intro:
            "Innenraum­verkleidungen, Motorraum­komponenten und Struktur­teile. Bioverstärkte Compounds, die Gewicht und CO₂ reduzieren — ohne Leistungs­einbußen.",
          bullets: ["IATF-konforme Dokumentation", "Wärme- & UV-stabilisiert", "Biobasis-Reporting"],
        },
        {
          name: "Verpackung",
          intro:
            "Lebensmittel­konforme Biopolymere und technische Compounds für starre und flexible Verpackungen. Validiert auf industriellen Verarbeitern in der gesamten EU.",
          bullets: ["EU 10/2011-konforme Typen", "Thermoformen & Spritzguss", "Kompostierbare Optionen"],
        },
        {
          name: "Konsumgüter",
          intro:
            "Langlebige Konsum­produkte, die Premium-Haptik mit zuverlässiger Leistung verbinden — biobasierte und holzfaserverstärkte Compounds für die Serien­produktion.",
          bullets: ["Premium-Matt­oberflächen", "Farbabgestimmte Serien", "Geringer Geruch & VOC"],
        },
        {
          name: "Möbel & Interieur",
          intro:
            "Authentische Holzoptik-Compounds und Naturfaser­komposite für Architektur- und Möbel­anwendungen — Material­ästhetik mit industrieller Reproduzier­barkeit.",
          bullets: ["Holzanteil 25–55 %", "Schleifbare Oberflächen", "Raumluft­qualität"],
        },
        {
          name: "Elektrotechnik & Elektronik",
          intro:
            "Flammhemmende technische Compounds, mineralgefüllte Typen und dimensions­stabile Formulierungen für Gehäuse, Steckverbinder und Strukturelemente.",
          bullets: ["UL-Klassen-Typen", "Geringer Verzug", "EMV-kompatible Optionen"],
        },
        {
          name: "Additive Fertigung",
          intro:
            "FDM-Filamente und AM-Granulate in Industrie­qualität mit konstanter Rheologie und Oberflächen­finish — für Prototypen und End-Use-Teile.",
          bullets: ["1,75 / 2,85 mm Filament", "AM-Pellet-Granulate", "Engineering- & Bio-Typen"],
        },
      ],
    },

    innovation: {
      hero_eyebrow: "Innovation",
      hero_title: "Material­wissenschaft. Industriell perfektioniert.",
      hero_intro:
        "Unsere Labore vereinen Polymerchemie, Rheologie, Thermoanalyse und Pilot­extrusion unter einem Dach — sie verbinden Forschung mit der Realität europäischer Fertigung.",
      phil_eyebrow: "F&E-Philosophie",
      phil_title: "Vom Molekül zum Formteil.",
      phil_intro:
        "Wir verstehen industrielle Material­entwicklung als Engineering-Disziplin, nicht als Marketing. Jedes Compound wird auf mehreren Ebenen charakterisiert — molekular, prozess- und anwendungs­bezogen — bevor es in eine Kunden­spezifikation aufgenommen wird.",
      cap_eyebrow: "Labor-Kompetenzen",
      cap_title: "Sechs Disziplinen, ein Entwicklungs­team.",
      method_eyebrow: "Methode",
      method_title: "DOE-basierte Entwicklung. Validiert für die Produktion.",
      method_intro:
        "Statistische Versuchs­planung kombiniert mit Pilotversuchen im industriellen Maßstab schafft robuste Prozess­fenster — gemessen unter realen Verarbeitungs­bedingungen, nicht theoretisch.",
      stats: [
        ["120+", "Aktive Formulierungen"],
        ["1,4 kt", "Jahres­kapazität"],
        ["48 Std.", "Muster-Durchlaufzeit"],
        ["EU", "Hergestellt in Lettland"],
      ],
      capabilities: [
        { title: "Polymerchemie", desc: "Biobasierte Polymer-Synthesewege, reaktive Verträglichkeits­vermittlung und Additiv­design — auf Basis erneuerbarer Rohstoffe." },
        { title: "Rheologie & Fließverhalten", desc: "Kapillar- und Rotations­rheometrie, MFI- und Prozess­spiegel-Tests zur Vorhersage des Verarbeiter­verhaltens vor dem Scale-up." },
        { title: "Thermoanalyse", desc: "DSC, TGA und HDT zur Erfassung von Kristallisation, Zersetzung und thermischer Last­fähigkeit unter realen Bedingungen." },
        { title: "Mechanische Prüfung", desc: "Zug-, Biege-, Schlag- und Kriech­charakterisierung — kalibriert nach ISO- und ASTM-Industrie­benchmarks." },
        { title: "Mikroskopie & Oberfläche", desc: "Optische und Elektronen­mikroskopie für Dispersion, Faser-Matrix-Grenz­fläche und Fehlermodus-Analyse." },
        { title: "Pilot-Engineering", desc: "Doppelschnecken-Prozess­engineering und Seitenein­bringung — Übersetzung der Laborchemie in industrielle Realität." },
      ],
    },

    sustainability: {
      hero_eyebrow: "Nachhaltigkeit",
      hero_title: "Engineering, kein Marketing.",
      hero_intro:
        "Nachhaltigkeit wird bei BALTIVEX als Material­engineering-Disziplin verstanden — gemessen, dokumentiert und mit derselben Sorgfalt verteidigt wie mechanische Leistung.",
      pillars_eyebrow: "Fünf Säulen",
      pillars_title_l1: "Material­entscheidungen",
      pillars_title_l2: "mit messbarer Wirkung.",
      pillars_intro:
        "Wir vermeiden abstrakte Umwelt­aussagen. Jede BALTIVEX-Material­entscheidung basiert auf Daten, Methodik und nachverfolgbarem Rohstoff — entwickelt für europäische Kunden, die ihre eigenen Nachhaltigkeits­aussagen berichten und verteidigen müssen.",
      pillar_label: "Säule",
      method_eyebrow: "Methodik",
      method_title: "Daten statt Geschichten.",
      method_intro:
        "Daten zu Kohlenstoff, Biobasis-Anteil und Lebensende werden als methodik­basierte Engineering-Eingaben bereitgestellt — nicht als Marketing-Assets. Unsere Kunden nutzen sie für belastbare Entscheidungen in geprüften industriellen Umgebungen.",
      pillars: [
        { code: "01", title: "Kreislaufwirtschaft", desc: "Compounds für mechanisches Recycling entwickelt, Verarbeitungs­fenster durch Pilotversuche kartiert und Material-Lebensende für Industriekunden dokumentiert." },
        { code: "02", title: "Erneuerbare Rohstoffe", desc: "Biobasierte Monomere, Naturfasern und Holzmehl aus zertifizierten europäischen Quellen — Ersatz fossiler Rohstoffe, wo es die Leistung erlaubt." },
        { code: "03", title: "Material­effizienz", desc: "Leichtbau, Teile­konsolidierung und Zykluszeit-Optimierung — Reduktion der eingebetteten Energie pro Kilogramm Fertigprodukt." },
        { code: "04", title: "Geringerer CO₂-Fußabdruck", desc: "Cradle-to-Gate-CO₂-Reporting auf Anfrage. Programme zur Biobasis-Erhöhung, die CO₂-eq pro kg Bauteil nachweislich senken." },
        { code: "05", title: "Verantwortungs­volle Innovation", desc: "Ehrliche, engineering-basierte Nachhaltigkeits­aussagen. Kein Greenwashing — nur verifizierte Daten und Methodik, die Kunden verteidigen können." },
      ],
    },

    about: {
      hero_eyebrow: "Über BALTIVEX",
      hero_title: "Ein europäisches Unternehmen für fortschrittliche Werkstoffe.",
      hero_intro:
        "BALTIVEX wurde in Lettland gegründet und entwickelt sowie fertigt fortschrittliche Biopolymer-Compounds, Biokomposite und technische Polymere — für Hersteller, Verarbeiter und Produkt­entwickler in ganz Europa.",
      who_eyebrow: "Wer wir sind",
      who_title_l1: "Ein Material­unternehmen,",
      who_title_l2: "aufgebaut wie ein Ingenieur­büro.",
      who_intro:
        "BALTIVEX ist um Polymer­ingenieure, Prozess­technologen und Material­wissenschaftler herum organisiert. Wir sind kein Startup, kein Händler und keine Umwelt-NGO — wir sind ein europäischer Material­hersteller, der Engineering-Sorgfalt und Dokumentation genauso ernst nimmt wie unsere Kunden.",
      principles_eyebrow: "Prinzipien",
      principles_title: "Vier Prinzipien, die jede Formulierung prägen.",
      principle_label: "Prinzip",
      hq_eyebrow: "Hauptsitz",
      hq_title_l1: "Bezirk Ogre, Lettland.",
      hq_title_l2: "Europäische Union.",
      hq_intro:
        "Unsere Fertigung und F&E befinden sich in Lettland und beliefern Kunden in der gesamten EU mit europäischen Industrie­standards und Logistik.",
      principles: [
        ["Engineering zuerst", "Material­entscheidungen treffen Polymer­ingenieure, nicht Marketing."],
        ["Europäische Fertigung", "Hergestellt in Lettland nach EU-Industrie­standards mit vollständiger Rückverfolgbarkeit."],
        ["Langfristige Partner­schaften", "Wir messen Erfolg in jahrzehnte­langen Kunden­beziehungen, nicht in Einzel­bestellungen."],
        ["Dokumentierte Qualität", "Jede Charge trägt CoA, SDS und nachverfolgbare Rohstoff­dokumentation."],
      ],
    },

    contact: {
      hero_eyebrow: "Kontakt",
      hero_title: "Briefen Sie unser Engineering-Team.",
      hero_intro:
        "Erzählen Sie uns von Ihrer Anwendung, Ihrem Verarbeitungs­prozess und Ihren Leistungszielen. Unsere Polymer­ingenieure antworten mit einer Material­richtung — in der Regel innerhalb von zwei Arbeitstagen.",
      hq_eyebrow: "Hauptsitz",
      hq_company: "BALTIVEX",
      hq_addr: "Bezirk Ogre, Lettland · Europäische Union",
      inquiry_eyebrow: "Für Ihre Anfrage",
      inquiry_items: [
        "· Material-Datenblätter und CoA auf Anfrage",
        "· Briefings zu kunden­spezifischen Formulierungen",
        "· Beratung zu Pilot­versuchen und Scale-up",
        "· Vertrieb & Logistik in der gesamten EU",
      ],
      f_name: "Vollständiger Name *",
      f_name_ph: "Max Mustermann",
      f_email: "E-Mail *",
      f_email_ph: "name@unternehmen.de",
      f_phone: "Telefon",
      f_phone_ph: "+49 ...",
      f_company: "Unternehmen",
      f_company_ph: "Name der Organisation",
      f_industry: "Branche",
      f_industry_ph: "Branche auswählen",
      f_interest: "Interesse",
      f_interest_ph: "Material / Leistung",
      f_message: "Nachricht *",
      f_message_ph:
        "Beschreiben Sie Ihre Anwendung, Ihren Verarbeitungs­prozess, Volumen und Leistungs­anforderungen.",
      privacy:
        "Mit dem Absenden stimmen Sie zu, dass Ihre Daten ausschließlich zur Beantwortung Ihrer Anfrage verwendet werden. Keine Marketing-E-Mails.",
      submit: "Anfrage senden",
      submitting: "Sende…",
      success: "Vielen Dank. Unser Engineering-Team meldet sich in Kürze.",
      err_name: "Bitte geben Sie Ihren vollständigen Namen ein.",
      err_email: "Bitte geben Sie eine gültige E-Mail ein.",
      err_message: "Bitte fügen Sie eine kurze Nachricht hinzu (mind. 10 Zeichen).",
      err_submit: "Senden fehlgeschlagen. Bitte erneut versuchen.",
      industry_options: [
        "Automobil",
        "Verpackung",
        "Konsumgüter",
        "Möbel & Interieur",
        "Elektrotechnik & Elektronik",
        "Additive Fertigung",
        "Sonstige",
      ],
      interest_options: [
        "PLA-Compounds",
        "Biokomposite",
        "Holzgefüllte Polymere",
        "Technische Compounds",
        "Masterbatch-Lösungen",
        "3D-Druck-Materialien",
        "Kundenspezifische Formulierung",
        "Pilot / Scale-up",
        "Sonstige",
      ],
    },

    notfound: {
      label: "Fehler · 404",
      title_l1: "Diese Seite gehört nicht",
      title_l2_a: "zum BALTIVEX-",
      title_l2_b: "Portfolio.",
      cta: "Zur Startseite",
    },
  },
};
